
import { supabase } from '@/integrations/supabase/client';
import { Company } from '@/lib/types/trek-types';
import { getImageUrl } from './utils';

/**
 * Get a specific company by ID
 */
export async function getCompanyById(id: string): Promise<Company | null> {
  try {
    // Use maybeSingle() instead of single() to handle the case when no data is found
    const { data, error } = await supabase
      .from('companies')
      .select('*')
      .eq('id', id)
      .maybeSingle();
      
    if (error) throw error;
    
    if (!data) {
      console.warn(`Company with ID ${id} not found in database`);
      return null;
    }
    
    let logo = data.logo;
    if (!logo) {
      try {
        const url = await getImageUrl(`companies/${id}_logo.png`);
        logo = url;
      } catch (error) {
        logo = `https://ui-avatars.com/api/?name=${encodeURIComponent(data.name)}&background=random`;
      }
    }
    
    return {
      id: data.id,
      name: data.name,
      description: data.description,
      foundedYear: data.founded_year,
      rating: data.rating,
      reviewCount: data.review_count,
      headquarters: data.headquarters || 'Unknown',
      primaryHighlights: data.primary_highlights || [],
      specialties: data.specialties || [],
      safetyRecord: data.safety_record || '',
      certifications: data.certifications || [],
      sustainabilityInitiatives: data.sustainability_initiatives || [],
      logo,
      is_active: data.is_active,
      is_featured: data.is_featured,
      priority: data.priority
    };
  } catch (error) {
    console.warn(`Error fetching company ${id} from Supabase:`, error);
    return null;
  }
}

/**
 * Get all companies
 */
export async function getAllCompanies(): Promise<Company[]> {
  try {
    const { data, error } = await supabase
      .from('companies')
      .select('*')
      .eq('is_active', true)
      .order('priority', { ascending: false, nullsFirst: false })
      .order('name');
      
    if (error) throw error;
    
    if (data && data.length > 0) {
      return await Promise.all(data.map(async company => {
        let logo = company.logo;
        if (!logo) {
          try {
            const url = await getImageUrl(`companies/${company.id}_logo.png`);
            logo = url;
          } catch (error) {
            logo = `https://ui-avatars.com/api/?name=${encodeURIComponent(company.name)}&background=random`;
          }
        }
        
        return {
          id: company.id,
          name: company.name,
          description: company.description,
          foundedYear: company.founded_year,
          rating: company.rating,
          reviewCount: company.review_count,
          headquarters: company.headquarters || 'Unknown',
          primaryHighlights: company.primary_highlights || [],
          specialties: company.specialties || [],
          safetyRecord: company.safety_record || '',
          certifications: company.certifications || [],
          sustainabilityInitiatives: company.sustainability_initiatives || [],
          logo,
          is_active: company.is_active,
          is_featured: company.is_featured,
          priority: company.priority
        };
      }));
    }
    
    return [];
  } catch (error) {
    console.warn('Error fetching companies from Supabase:', error);
    return [];
  }
}

/**
 * Get featured companies
 */
export async function getFeaturedCompanies(limit = 4): Promise<Company[]> {
  try {
    const { data, error } = await supabase
      .from('companies')
      .select('*')
      .eq('is_active', true)
      .eq('is_featured', true)
      .order('priority', { ascending: false, nullsFirst: false })
      .order('rating', { ascending: false })
      .limit(limit);
      
    if (error) throw error;
    
    if (data && data.length > 0) {
      return await Promise.all(data.map(async company => {
        let logo = company.logo;
        if (!logo) {
          try {
            const url = await getImageUrl(`companies/${company.id}_logo.png`);
            logo = url;
          } catch (error) {
            logo = `https://ui-avatars.com/api/?name=${encodeURIComponent(company.name)}&background=random`;
          }
        }
        
        return {
          id: company.id,
          name: company.name,
          description: company.description,
          foundedYear: company.founded_year,
          rating: company.rating,
          reviewCount: company.review_count,
          headquarters: company.headquarters || 'Unknown',
          primaryHighlights: company.primary_highlights || [],
          specialties: company.specialties || [],
          safetyRecord: company.safety_record || '',
          certifications: company.certifications || [],
          sustainabilityInitiatives: company.sustainability_initiatives || [],
          logo,
          is_active: company.is_active,
          is_featured: company.is_featured,
          priority: company.priority
        };
      }));
    }
    
    return [];
  } catch (error) {
    console.warn('Error fetching featured companies from Supabase:', error);
    return [];
  }
}
